import { hasExactly, intWithin, isPlainRecord, marketRuleSetting, resolveMarketRule } from './market-rule-kit.js';
export const DRIVER_REFERRAL_KEY = 'promotion.driverReferral';
/** The structural bounds of every member (the database floor mirrors them, m0251). */
export const DRIVER_REFERRAL_BOUNDS = {
    maxBudgetShareBp: { min: 1, max: 10_000 },
    maxRidesRequired: { min: 1, max: 2_000 },
    maxCompletionWindowDays: { min: 1, max: 365 },
    maxCampaignDays: { min: 1, max: 366 },
    maxPerReferrerCap: { min: 1, max: 1_000 },
    attributionWindowDays: { min: 1, max: 90 },
};
const MEMBERS = ['enabled', 'maxBudgetShareBp', 'maxRidesRequired', 'maxCompletionWindowDays', 'maxCampaignDays', 'maxPerReferrerCap', 'attributionWindowDays'];
/** The recommended value: on; a campaign up to 20 % of the monthly coupon budget, up to 500 rides in up to 90 days, a campaign of up to 120 days, 20 drivers per rider, 14 days to attach the code. */
export const DEFAULT_DRIVER_REFERRAL = {
    enabled: true,
    maxBudgetShareBp: 2_000,
    maxRidesRequired: 500,
    maxCompletionWindowDays: 90,
    maxCampaignDays: 120,
    maxPerReferrerCap: 20,
    attributionWindowDays: 14,
};
/** An unreadable version: no driver invitation (and every bound at its smallest). */
export const STRICT_DRIVER_REFERRAL = {
    enabled: false,
    maxBudgetShareBp: DRIVER_REFERRAL_BOUNDS.maxBudgetShareBp.min,
    maxRidesRequired: DRIVER_REFERRAL_BOUNDS.maxRidesRequired.min,
    maxCompletionWindowDays: DRIVER_REFERRAL_BOUNDS.maxCompletionWindowDays.min,
    maxCampaignDays: DRIVER_REFERRAL_BOUNDS.maxCampaignDays.min,
    maxPerReferrerCap: DRIVER_REFERRAL_BOUNDS.maxPerReferrerCap.min,
    attributionWindowDays: DRIVER_REFERRAL_BOUNDS.attributionWindowDays.min,
};
export const DRIVER_REFERRAL_POLICY_REASON_CODES = ['DRIVER_REFERRAL_POLICY_GOVERNED', 'DRIVER_REFERRAL_POLICY_DEFAULT', 'DRIVER_REFERRAL_POLICY_INVALID'];
const ISSUE_OF = {
    maxBudgetShareBp: 'INVALID_MAX_BUDGET_SHARE_BP',
    maxRidesRequired: 'INVALID_MAX_RIDES_REQUIRED',
    maxCompletionWindowDays: 'INVALID_MAX_COMPLETION_WINDOW_DAYS',
    maxCampaignDays: 'INVALID_MAX_CAMPAIGN_DAYS',
    maxPerReferrerCap: 'INVALID_MAX_PER_REFERRER_CAP',
    attributionWindowDays: 'INVALID_ATTRIBUTION_WINDOW_DAYS',
};
export function driverReferralPolicyIssue(v) {
    if (!isPlainRecord(v))
        return 'NOT_AN_OBJECT';
    if (!hasExactly(v, MEMBERS))
        return 'UNKNOWN_OR_MISSING_MEMBER';
    if (typeof v.enabled !== 'boolean')
        return 'INVALID_ENABLED';
    for (const [member, b] of Object.entries(DRIVER_REFERRAL_BOUNDS)) {
        if (!intWithin(v[member], b.min, b.max))
            return ISSUE_OF[member];
    }
    return undefined;
}
const copy = (v) => ({
    enabled: v.enabled,
    maxBudgetShareBp: v.maxBudgetShareBp,
    maxRidesRequired: v.maxRidesRequired,
    maxCompletionWindowDays: v.maxCompletionWindowDays,
    maxCampaignDays: v.maxCampaignDays,
    maxPerReferrerCap: v.maxPerReferrerCap,
    attributionWindowDays: v.attributionWindowDays,
});
export const DRIVER_REFERRAL_SPEC = {
    key: DRIVER_REFERRAL_KEY,
    issue: driverReferralPolicyIssue,
    copy,
    defaultValue: DEFAULT_DRIVER_REFERRAL,
    strictValue: STRICT_DRIVER_REFERRAL,
    reasonPrefix: 'DRIVER_REFERRAL_POLICY',
    constraintClass: 'ledger',
    // A lower scope never loosens a money ceiling: off anywhere above is off, every bound the smaller of the two.
    tighten: (d, e) => ({
        enabled: d.enabled && e.enabled,
        maxBudgetShareBp: Math.min(d.maxBudgetShareBp, e.maxBudgetShareBp),
        maxRidesRequired: Math.min(d.maxRidesRequired, e.maxRidesRequired),
        maxCompletionWindowDays: Math.min(d.maxCompletionWindowDays, e.maxCompletionWindowDays),
        maxCampaignDays: Math.min(d.maxCampaignDays, e.maxCampaignDays),
        maxPerReferrerCap: Math.min(d.maxPerReferrerCap, e.maxPerReferrerCap),
        attributionWindowDays: Math.min(d.attributionWindowDays, e.attributionWindowDays),
    }),
};
export function driverReferralPolicySetting(input) {
    return marketRuleSetting(DRIVER_REFERRAL_SPEC, input);
}
export function resolveDriverReferralPolicy(settings, marketId, at, regionCode) {
    return resolveMarketRule(DRIVER_REFERRAL_SPEC, settings, marketId, at, regionCode);
}
//# sourceMappingURL=driver-referral-policy.js.map